import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { z } from "zod";

import { formatTime, parseTime, targetTotalMinutes, type TargetTime } from "@/lib/time";
import { ElevationProfile } from "@/tools/race-strategy/components/ElevationProfile";
import { RouteMap } from "@/tools/race-strategy/components/RouteMap";
import { fetchCourse, fetchManifest, fetchRoute } from "@/tools/race-strategy/data";
import {
  type Conditions,
  generateStrategy,
  type PacedSegment,
  type Strategy,
  type WeatherCondition,
} from "@/tools/race-strategy/engine";
import type { Course, EventManifestEntry, Manifest } from "@/tools/race-strategy/schemas";

const SearchSchema = z.object({
  event: z.string().optional(),
  distance: z.string().optional(),
  t: z.string().optional(),
  temp: z.enum(["cool", "normal", "hot"]).optional(),
});

export const Route = createFileRoute("/foundry/race-strategy")({
  validateSearch: (s) => SearchSchema.parse(s),
  component: RaceStrategyPage,
});

function defaultTargetForKm(km: number): TargetTime {
  if (km <= 10) return { hours: 0, minutes: 58, seconds: 0 };
  if (km <= 21.5) return { hours: 2, minutes: 0, seconds: 0 };
  if (km <= 25) return { hours: 2, minutes: 30, seconds: 0 };
  return { hours: 4, minutes: 0, seconds: 0 };
}

function RaceStrategyPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const view: "form" | "results" = search.event && search.distance && search.t ? "results" : "form";

  const manifestQuery = useQuery<Manifest>({
    queryKey: ["race-strategy", "manifest"],
    queryFn: fetchManifest,
  });

  // Selected event/distance — defaults from URL or first verified entry in manifest
  const manifest = manifestQuery.data;
  const selectedEvent: EventManifestEntry | undefined = useMemo(() => {
    if (!manifest) return undefined;
    if (search.event) {
      const ev = manifest.events.find((e) => e.slug === search.event);
      if (ev) return ev;
    }
    return manifest.events[0];
  }, [manifest, search.event]);

  const selectedDistance = useMemo(() => {
    if (!selectedEvent) return undefined;
    if (search.distance) {
      const d = selectedEvent.distances.find(
        (d) => d.slug === search.distance && d.status !== "coming_soon",
      );
      if (d) return d;
    }
    return (
      selectedEvent.distances.find((d) => d.status === "verified") ??
      selectedEvent.distances.find((d) => d.status === "provisional") ??
      selectedEvent.distances.find((d) => d.status !== "coming_soon")
    );
  }, [selectedEvent, search.distance]);

  const courseQuery = useQuery<Course | null>({
    queryKey: ["race-strategy", "course", selectedDistance?.course_file],
    queryFn: () =>
      selectedDistance?.course_file ? fetchCourse(selectedDistance.course_file) : null,
    enabled: !!selectedDistance?.course_file,
  });

  const course = courseQuery.data ?? null;

  const routeQuery = useQuery({
    queryKey: ["race-strategy", "route", course?.route_file],
    queryFn: () => (course?.route_file ? fetchRoute(course.route_file) : null),
    enabled: !!course?.route_file,
  });
  const routeGeojson = routeQuery.data ?? null;

  // Target time — URL hydrates if present, else default for this distance
  const initialTime = parseTime(search.t ?? "") ?? defaultTargetForKm(course?.distance_km ?? 10);
  const [targetTime, setTargetTime] = useState<TargetTime>(initialTime);
  // When course changes (and we're on form view without t), reset target to default
  useEffect(() => {
    if (!course) return;
    if (search.t) return; // URL is source of truth
    setTargetTime(defaultTargetForKm(course.distance_km));
  }, [course, search.t]);

  const [conditions, setConditions] = useState<Conditions>({ temp: search.temp ?? "normal" });
  useEffect(() => {
    if (search.temp) setConditions({ temp: search.temp });
  }, [search.temp]);

  const totalTargetMinutes = targetTotalMinutes(targetTime);
  const strategy: Strategy | null = useMemo(() => {
    if (view !== "results" || !course) return null;
    return generateStrategy(course, totalTargetMinutes, conditions);
  }, [view, course, totalTargetMinutes, conditions]);

  // True when the next render of ResultsView is fresh from a Generate click,
  // so we autoplay the story on initial mount. False when hydrating from URL
  // (shared link or page reload) — we don't want unsolicited autoplay there.
  const autoplayOnNextResults = useRef(false);

  // Wizard transitions
  const generate = () => {
    if (!selectedEvent || !selectedDistance) return;
    autoplayOnNextResults.current = true;
    navigate({
      search: {
        event: selectedEvent.slug,
        distance: selectedDistance.slug,
        t: formatTime(targetTime),
        temp: conditions.temp,
      },
    });
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const editPlan = () => {
    autoplayOnNextResults.current = false;
    navigate({ search: {} });
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  if (manifestQuery.error) {
    return (
      <div className="p-10 text-center font-mono text-foundry-danger">
        Error loading catalogue: {(manifestQuery.error as Error).message}
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className="p-16 text-center font-mono text-sm text-foundry-text-dim">
        Loading course catalogue…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-foundry-bg text-foundry-text">
      <ForgeNav />
      {view === "form" ? (
        <CompactHero />
      ) : strategy && course ? (
        <InputEcho
          course={course}
          targetTime={targetTime}
          strategy={strategy}
          temp={conditions.temp}
          onEdit={editPlan}
        />
      ) : null}

      <div className="mx-auto max-w-[1180px] px-6 pb-16 pt-7">
        {view === "form" && (
          <FormView
            manifest={manifest}
            selectedEvent={selectedEvent}
            selectedDistance={selectedDistance}
            course={course}
            courseLoading={courseQuery.isLoading}
            targetTime={targetTime}
            setTargetTime={setTargetTime}
            conditions={conditions}
            setConditions={setConditions}
            onGenerate={generate}
            onEventChange={(slug) => {
              const ev = manifest.events.find((e) => e.slug === slug);
              if (!ev) return;
              const first =
                ev.distances.find((d) => d.status === "verified") ??
                ev.distances.find((d) => d.status === "provisional") ??
                ev.distances.find((d) => d.status !== "coming_soon");
              navigate({ search: { ...search, event: slug, distance: first?.slug } });
            }}
            onDistanceChange={(slug) => {
              navigate({ search: { ...search, distance: slug } });
            }}
          />
        )}

        {view === "results" && !strategy && (
          <div className="p-16 text-center font-mono text-sm text-foundry-text-dim">
            Loading strategy…
          </div>
        )}

        {view === "results" && strategy && course && (
          <ResultsView
            strategy={strategy}
            course={course}
            routeGeojson={routeGeojson}
            autoStart={autoplayOnNextResults.current}
          />
        )}
      </div>
    </div>
  );
}

// -------- Sticky nav --------
function ForgeNav() {
  return (
    <nav
      className="sticky top-0 z-50 flex items-center justify-between border-b border-foundry-border bg-[rgba(7,7,11,0.92)] px-6 py-3 backdrop-blur"
      style={{ fontFamily: "var(--foundry-font-mono)" }}
    >
      <div className="flex items-center gap-2">
        <a href="/" className="text-xs text-foundry-text-dim hover:text-foundry-accent">
          ← ai.aakashgoel.com
        </a>
        <span className="text-[10px] tracking-wider text-foundry-text-dim">/foundry/race-strategy</span>
      </div>
      <a href="/" className="text-xs text-foundry-text-dim hover:text-foundry-accent">
        all tools
      </a>
    </nav>
  );
}

function CompactHero() {
  return (
    <div className="border-b border-foundry-border px-6 py-3.5">
      <div className="mx-auto flex max-w-[1180px] flex-wrap items-baseline gap-3.5">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-foundry-accent">
          Race Strategy
        </span>
        <span className="font-sans text-[13px] text-foundry-text-muted">
          Km-by-km pacing for Indian races, every assumption cited.
        </span>
      </div>
    </div>
  );
}

function InputEcho({
  course,
  targetTime,
  strategy,
  temp,
  onEdit,
}: {
  course: Course;
  targetTime: TargetTime;
  strategy: Strategy;
  temp: WeatherCondition;
  onEdit: () => void;
}) {
  const avgPaceLabel = `${Math.floor(strategy.avgPace)}:${String(
    Math.round((strategy.avgPace % 1) * 60),
  ).padStart(2, "0")}/km avg`;
  const tempSub = temp === "cool" ? "<20°C" : temp === "hot" ? ">25°C" : "20–25°C";

  return (
    <div
      className="border-b border-foundry-border px-6 py-5"
      style={{ background: "linear-gradient(180deg, rgba(255,107,107,0.04), rgba(255,107,107,0))" }}
    >
      <div className="mx-auto flex max-w-[1180px] flex-wrap items-stretch gap-0">
        <Tile label="Event" labelColor="#FF6B6B" big={course.event} sub={`${course.city} · ${course.next_edition}`} flex="2 1 240px" />
        <Tile label="Distance" big={course.distance_label} sub={`${course.distance_km} km`} flex="1 1 110px" />
        <Tile label="Target" big={formatTime(targetTime)} sub={avgPaceLabel} flex="1 1 110px" />
        <Tile label="Conditions" big={temp[0]!.toUpperCase() + temp.slice(1)} sub={tempSub} flex="1 1 110px" />
        <div className="flex items-center pl-4 ml-auto">
          <button
            onClick={onEdit}
            type="button"
            className="cursor-pointer whitespace-nowrap rounded-md border border-foundry-accent/30 bg-foundry-accent/5 px-3.5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-foundry-accent"
            style={{ fontFamily: "'DM Mono', monospace" }}
          >
            ← Edit Plan
          </button>
        </div>
      </div>
    </div>
  );
}

function Tile({
  label,
  labelColor = "#666",
  big,
  sub,
  flex,
}: {
  label: string;
  labelColor?: string;
  big: string;
  sub: string;
  flex: string;
}) {
  return (
    <div
      className="border-r border-foundry-border pr-[18px] pl-[18px] first:pl-0 last:border-r-0"
      style={{ flex, minWidth: 100 }}
    >
      <div
        className="mb-1 text-[9px] font-bold uppercase tracking-[0.14em]"
        style={{ color: labelColor }}
      >
        {label}
      </div>
      <div className="font-sans text-lg font-extrabold leading-tight text-white">{big}</div>
      <div className="mt-0.5 font-sans text-[11px] text-foundry-text-muted">{sub}</div>
    </div>
  );
}

// -------- Form view --------
function FormView({
  manifest,
  selectedEvent,
  selectedDistance,
  course,
  courseLoading,
  targetTime,
  setTargetTime,
  conditions,
  setConditions,
  onGenerate,
  onEventChange,
  onDistanceChange,
}: {
  manifest: Manifest;
  selectedEvent: EventManifestEntry | undefined;
  selectedDistance: EventManifestEntry["distances"][number] | undefined;
  course: Course | null;
  courseLoading: boolean;
  targetTime: TargetTime;
  setTargetTime: (t: TargetTime) => void;
  conditions: Conditions;
  setConditions: (c: Conditions) => void;
  onGenerate: () => void;
  onEventChange: (slug: string) => void;
  onDistanceChange: (slug: string) => void;
}) {
  return (
    <div className="mx-auto max-w-[720px]">
      {/* Event */}
      <div className="mb-5">
        <FieldLabel>Event</FieldLabel>
        <div className="flex flex-wrap gap-2">
          {manifest.events.map((ev) => {
            const sel = ev.slug === selectedEvent?.slug;
            return (
              <button
                key={ev.slug}
                type="button"
                onClick={() => onEventChange(ev.slug)}
                className={`cursor-pointer rounded-lg border px-4 py-2.5 text-left text-xs font-semibold leading-tight ${
                  sel
                    ? "border-foundry-accent bg-foundry-accent/10 text-foundry-accent"
                    : "border-foundry-border bg-white/[0.02] text-foundry-text-muted"
                }`}
                style={{ fontFamily: "inherit" }}
              >
                <div>{ev.name}</div>
                <div
                  className="mt-0.5 text-[10px] font-normal"
                  style={{ color: sel ? "rgba(255,107,107,0.6)" : "var(--foundry-text-dim)" }}
                >
                  {ev.city} · {ev.month}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Distance */}
      {selectedEvent && (
        <div className="mb-6">
          <FieldLabel>Distance</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {selectedEvent.distances.map((dist) => {
              const sel = dist.slug === selectedDistance?.slug;
              const disabled = dist.status === "coming_soon";
              const provisional = dist.status === "provisional";
              const accent = provisional ? "#FFB84D" : "#4DABF7";
              const sub = disabled
                ? "coming soon"
                : provisional
                  ? `provisional · based on ${dist.based_on_year}`
                  : "verified";
              return (
                <button
                  key={dist.slug}
                  type="button"
                  disabled={disabled}
                  onClick={() => onDistanceChange(dist.slug)}
                  className={`rounded-lg border px-4 py-2.5 text-left text-xs font-semibold leading-tight ${
                    disabled
                      ? "cursor-not-allowed opacity-50 border-dashed"
                      : "cursor-pointer"
                  }`}
                  style={{
                    borderColor: sel ? accent : disabled ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.08)",
                    background: sel
                      ? provisional
                        ? "rgba(255,184,77,0.08)"
                        : "rgba(77,171,247,0.08)"
                      : "rgba(255,255,255,0.02)",
                    color: sel ? accent : disabled ? "#444" : "#999",
                  }}
                >
                  <div>{dist.label}</div>
                  <div
                    className="mt-0.5 text-[10px] font-normal"
                    style={{ color: sel ? `${accent}99` : "#555" }}
                  >
                    {dist.km}km · {sub}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Course summary */}
      {courseLoading && (
        <div className="p-6 text-center text-xs text-foundry-text-dim">Loading course data…</div>
      )}
      {course && !courseLoading && (
        <>
          <CourseSummaryRow course={course} />
          {course.status === "provisional" && <ProvisionalBanner course={course} />}
          <CourseContext course={course} />
          <TargetTimeInput course={course} targetTime={targetTime} setTargetTime={setTargetTime} />
          <ConditionsInput conditions={conditions} setConditions={setConditions} />
          <button
            type="button"
            onClick={onGenerate}
            className="w-full cursor-pointer rounded-xl border-0 px-4 py-4 font-sans text-sm font-extrabold uppercase tracking-wider text-white"
            style={{
              background: "linear-gradient(135deg, #FF6B6B, #EE5A24)",
              boxShadow: "0 4px 20px rgba(255,107,107,0.25)",
            }}
          >
            Generate Race Strategy →
          </button>
        </>
      )}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-2.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-foundry-text-muted">
      {children}
    </label>
  );
}

function CourseSummaryRow({ course }: { course: Course }) {
  const items = [
    { label: "Distance", value: `${course.distance_km} km` },
    { label: "Typical Temp", value: `${course.typical_temp_c}°C` },
    { label: "Humidity", value: `${course.typical_humidity_pct}%` },
    { label: "Altitude", value: `${course.altitude_m}m ASL` },
    { label: "Next Edition", value: course.next_edition },
  ];
  return (
    <div className="mb-3 flex flex-wrap gap-6 rounded-xl border border-foundry-border bg-white/[0.02] px-5 py-4">
      {items.map((it) => (
        <div key={it.label}>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-foundry-text-muted">
            {it.label}
          </div>
          <div className="mt-0.5 text-sm font-bold text-foundry-text">{it.value}</div>
        </div>
      ))}
    </div>
  );
}

function ProvisionalBanner({ course }: { course: Course }) {
  return (
    <div className="mb-3.5 flex items-start gap-3 rounded-xl border border-amber-400/25 bg-amber-400/[0.08] px-4.5 py-3.5">
      <div className="text-lg leading-none mt-px">⚠️</div>
      <div className="flex-1">
        <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-foundry-warning">
          Provisional Course · Based on {course.based_on_year} Edition
        </div>
        <div className="font-sans text-xs leading-relaxed" style={{ color: "#caa969" }}>
          {course.provisional_note ??
            `This course reflects the ${course.based_on_year} edition. Awaiting official next-edition route map confirmation. Treat as a strong planning approximation.`}
        </div>
      </div>
    </div>
  );
}

function CourseContext({ course }: { course: Course }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between rounded-xl border border-blue-300/15 bg-blue-300/[0.04] px-4.5 py-3 text-left text-xs font-bold uppercase tracking-wider text-foundry-info"
      >
        <span>📖 Course Context · Sources & Reasoning</span>
        <span className="text-sm">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="ctx-prose -mt-px rounded-b-xl border border-t-0 border-foundry-border bg-white/[0.015] px-5 py-5">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{course.body}</ReactMarkdown>
          <div className="mt-4 border-t border-foundry-border pt-4">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-foundry-text-muted">
              Sources Verified Against
            </div>
            <ul className="m-0 list-disc pl-4 font-sans text-[13px] leading-relaxed text-foundry-text-muted">
              {course.sources.map((s, i) => (
                <li key={i}>
                  {s.url ? (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foundry-text underline decoration-white/20"
                    >
                      {s.title}
                    </a>
                  ) : (
                    <strong className="text-foundry-text">{s.title}</strong>
                  )}
                  {s.note && <span className="text-foundry-text-dim"> — {s.note}</span>}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2 border-t border-foundry-border pt-3.5">
            <div className="font-sans text-[11px] text-foundry-text-muted">
              Last verified: <span className="text-foundry-text">{course.last_verified}</span>
            </div>
            <a
              href={course.source_path}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] tracking-wide text-foundry-info"
            >
              → view raw source markdown
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function TargetTimeInput({
  course,
  targetTime,
  setTargetTime,
}: {
  course: Course;
  targetTime: TargetTime;
  setTargetTime: (t: TargetTime) => void;
}) {
  return (
    <div className="mb-6">
      <FieldLabel>Target Finish Time</FieldLabel>
      <div className="flex items-center gap-3">
        {course.distance_km > 15 && (
          <>
            <NumInput
              label="HRS"
              value={targetTime.hours}
              max={8}
              onChange={(v) => setTargetTime({ ...targetTime, hours: v })}
            />
            <span className="text-2xl font-light text-foundry-text-dim">:</span>
          </>
        )}
        <NumInput
          label="MIN"
          value={targetTime.minutes}
          max={59}
          onChange={(v) => setTargetTime({ ...targetTime, minutes: v })}
        />
        <span className="text-2xl font-light text-foundry-text-dim">:</span>
        <NumInput
          label="SEC"
          value={targetTime.seconds}
          max={59}
          onChange={(v) => setTargetTime({ ...targetTime, seconds: v })}
        />
      </div>
    </div>
  );
}

function NumInput({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: number;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <input
        type="number"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(Number.parseInt(e.target.value, 10) || 0)}
        className="w-16 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-3 text-center text-xl font-bold text-white"
        style={{ fontFamily: "inherit" }}
      />
      <div className="mt-1 text-center text-[10px] text-foundry-text-muted">{label}</div>
    </div>
  );
}

function ConditionsInput({
  conditions,
  setConditions,
}: {
  conditions: Conditions;
  setConditions: (c: Conditions) => void;
}) {
  const opts: { id: WeatherCondition; label: string; sub: string }[] = [
    { id: "cool", label: "Cool", sub: "<20°C" },
    { id: "normal", label: "Normal", sub: "20-25°C" },
    { id: "hot", label: "Hot", sub: ">25°C" },
  ];
  return (
    <div className="mb-7">
      <FieldLabel>Expected Conditions</FieldLabel>
      <div className="flex gap-2">
        {opts.map(({ id, label, sub }) => {
          const sel = conditions.temp === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setConditions({ temp: id })}
              className="cursor-pointer rounded-lg border px-4.5 py-2.5 text-xs font-semibold"
              style={{
                borderColor: sel ? "#4DABF7" : "rgba(255,255,255,0.08)",
                background: sel ? "rgba(77,171,247,0.08)" : "rgba(255,255,255,0.02)",
                color: sel ? "#4DABF7" : "#888",
                fontFamily: "inherit",
              }}
            >
              {label} <span className="text-[10px] opacity-60">{sub}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -------- Results --------
function ResultsView({
  strategy,
  course,
  routeGeojson,
  autoStart,
}: {
  strategy: Strategy;
  course: Course;
  routeGeojson: ReturnType<typeof Object.assign> | null;
  autoStart: boolean;
}) {
  const [expandedKm, setExpandedKm] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(autoStart);
  const [storyDuration, setStoryDuration] = useState(7000);
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Story auto-advance
  useEffect(() => {
    if (!isPlaying) return;
    const total = strategy.segments.length;
    setExpandedKm((prev) => (prev == null ? 0 : prev));
    const id = window.setInterval(() => {
      setExpandedKm((prev) => {
        const next = prev == null ? 0 : prev + 1;
        if (next >= total) {
          setIsPlaying(false);
          return prev;
        }
        return next;
      });
    }, storyDuration);
    return () => window.clearInterval(id);
  }, [isPlaying, strategy.segments.length, storyDuration]);

  // Scroll active card into view while playing
  useEffect(() => {
    if (!isPlaying || expandedKm == null) return;
    cardRefs.current[expandedKm]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [expandedKm, isPlaying]);

  const playToggle = () => {
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }
    if (expandedKm != null && expandedKm >= strategy.segments.length - 1) {
      setExpandedKm(null);
    }
    setIsPlaying(true);
  };

  const minPace = Math.min(...strategy.segments.map((s) => s.pace));
  const maxPace = Math.max(...strategy.segments.map((s) => s.pace));
  const half = Math.ceil(strategy.segments.length / 2);
  const firstHalf = strategy.segments.slice(0, half);
  const secondHalf = strategy.segments.slice(half);
  const splitDiff =
    firstHalf.reduce((s, x) => s + x.pace, 0) / firstHalf.length -
    secondHalf.reduce((s, x) => s + x.pace, 0) / secondHalf.length;

  const focusKm =
    expandedKm != null ? strategy.segments[expandedKm]?.km ?? null : null;

  return (
    <div>
      {/* Summary */}
      <div className="mx-auto mb-6 flex max-w-[720px] flex-wrap justify-between gap-7 rounded-xl border border-foundry-accent/15 bg-foundry-accent/[0.04] px-6 py-5">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-foundry-accent">
            Projected Finish
          </div>
          <div className="mt-1 font-sans text-3xl font-extrabold text-white">
            {strategy.projectedFinish}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-foundry-text-muted">
            Avg Pace
          </div>
          <div className="mt-2 text-xl font-bold text-foundry-text">
            {Math.floor(strategy.avgPace)}:
            {String(Math.round((strategy.avgPace % 1) * 60)).padStart(2, "0")} /km
          </div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-foundry-text-muted">
            Recommended Approach
          </div>
          <div className="mt-2 text-xl font-bold text-foundry-positive">Negative split</div>
          <div className="mt-px text-[11px] text-foundry-text-muted">
            ~{Math.abs(splitDiff * 60).toFixed(0)}s/km faster in 2nd half
          </div>
        </div>
      </div>

      <div className={`strategy-grid ${routeGeojson ? "" : "strategy-grid--no-map"}`}>
        <div>
          {/* Header + play controls */}
          <div className="mb-4 flex flex-wrap items-center gap-2.5">
            <h2 className="m-0 font-sans text-base font-extrabold text-foundry-text-bright">
              Km-by-Km Strategy
            </h2>
            <span className="text-[10px] tracking-wider text-foundry-text-muted">
              {isPlaying
                ? `PLAYING · KM ${focusKm ?? 1} OF ${
                    strategy.segments[strategy.segments.length - 1]?.km ?? 0
                  }`
                : routeGeojson
                  ? "TAP TO ZOOM MAP + DETAILS"
                  : "TAP FOR DETAILS"}
            </span>
            <div className="ml-auto flex items-center gap-2">
              <select
                value={storyDuration}
                onChange={(e) => setStoryDuration(Number.parseInt(e.target.value, 10))}
                className="cursor-pointer rounded-md border border-white/10 bg-white/[0.04] px-2 py-1.5 text-[11px] text-foundry-text-muted"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                <option value={5000}>5s/km</option>
                <option value={7000}>7s/km</option>
                <option value={10000}>10s/km</option>
              </select>
              <button
                type="button"
                onClick={playToggle}
                className="flex cursor-pointer items-center gap-1.5 rounded-md px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider"
                style={{
                  border: `1px solid ${isPlaying ? "rgba(255,184,77,0.4)" : "rgba(81,207,102,0.35)"}`,
                  background: isPlaying ? "rgba(255,184,77,0.08)" : "rgba(81,207,102,0.08)",
                  color: isPlaying ? "#FFB84D" : "#51CF66",
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                <span className="text-[10px]">{isPlaying ? "❚❚" : "▶"}</span>
                {isPlaying
                  ? "Pause"
                  : expandedKm != null && expandedKm >= strategy.segments.length - 1
                    ? "Replay"
                    : "Play Story"}
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            {strategy.segments.map((seg, idx) => (
              <KmCard
                key={idx}
                idx={idx}
                seg={seg}
                isExpanded={expandedKm === idx}
                isPlaying={isPlaying}
                isLast={idx === strategy.segments.length - 1}
                minPace={minPace}
                maxPace={maxPace}
                storyDuration={storyDuration}
                cardRef={(el) => {
                  cardRefs.current[idx] = el;
                }}
                onClick={() => {
                  if (isPlaying) setIsPlaying(false);
                  setExpandedKm(expandedKm === idx ? null : idx);
                }}
              />
            ))}
          </div>

          <div className="mt-7 rounded-xl border border-foundry-border bg-white/[0.02] px-6 py-5 font-sans text-xs leading-relaxed text-foundry-text-muted">
            <div className="mb-2 font-sans text-xs font-bold text-foundry-text-muted">
              Strategy Notes
            </div>
            This strategy uses a <strong className="text-foundry-positive">negative split</strong>{" "}
            approach — start conservatively, close hard. The first 30% is banked as buffer against
            crowd congestion and adrenaline. The recommended commit point is around km{" "}
            {Math.ceil(course.distance_km * 0.6)}.
            {course.altitude_pace_penalty_sec_per_km && (
              <>
                {" "}
                At {course.altitude_m}m altitude, paces add +
                {course.altitude_pace_penalty_sec_per_km}s/km vs sea level — a mid-range estimate
                from the Péronnet aerobic-power formula. Unacclimatised runners may pay closer to
                +80s/km. Breathe first, race second.
              </>
            )}
            {course.event_slug === "tata-steel-kolkata-25k" &&
              " Treat this as a marathon-pace dress rehearsal. Don't race it. Practice fueling, pacing, mental management."}
          </div>
        </div>

        {routeGeojson && (
          <div className="strategy-grid__map">
            <div className="mb-2.5 flex items-center gap-2.5">
              <h2 className="m-0 font-sans text-sm font-extrabold text-foundry-text">
                Course Map
              </h2>
              <span className="text-[10px] tracking-wider text-foundry-text-muted">
                {focusKm != null ? `KM ${focusKm - 1} → ${focusKm}` : "FULL ROUTE"}
              </span>
            </div>
            <div className="elevation-overlay">
              <ElevationProfile
                geojson={routeGeojson}
                segments={strategy.segments}
                focusKm={focusKm}
              />
            </div>
            <div className="route-map elevation-attached">
              <RouteMap geojson={routeGeojson} focusKm={focusKm} />
            </div>
            {course.route_source && (
              <div className="mt-2 font-sans text-[10px] text-foundry-text-muted">
                Route trace: {course.route_source}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 text-center text-[11px] text-foundry-text-dim">
        Race Strategy Calculator · v0.5 · elevation profile
      </div>
    </div>
  );
}

function KmCard({
  idx,
  seg,
  isExpanded,
  isPlaying,
  isLast,
  minPace,
  maxPace,
  storyDuration,
  cardRef,
  onClick,
}: {
  idx: number;
  seg: PacedSegment;
  isExpanded: boolean;
  isPlaying: boolean;
  isLast: boolean;
  minPace: number;
  maxPace: number;
  storyDuration: number;
  cardRef: (el: HTMLDivElement | null) => void;
  onClick: () => void;
}) {
  return (
    <div
      ref={cardRef}
      onClick={onClick}
      style={{
        background: isExpanded ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.015)",
        border: `1px solid ${isExpanded ? "rgba(255,107,107,0.2)" : "rgba(255,255,255,0.04)"}`,
      }}
      className={`relative cursor-pointer rounded-xl transition-all ${
        isExpanded ? "px-4.5 py-4" : "px-4.5 py-3"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-[13px] font-extrabold"
          style={{
            background: isLast ? "rgba(255,107,107,0.15)" : "rgba(255,255,255,0.04)",
            color: isLast ? "#FF6B6B" : "#888",
          }}
        >
          {seg.km}
        </div>
        <div style={{ flex: "0 0 60px" }}>
          <div className="text-base font-extrabold text-white">{seg.paceDisplay}</div>
          <div className="text-[9px] text-foundry-text-muted">/km</div>
        </div>
        <div className="min-w-[60px] flex-1">
          <PaceBar pace={seg.pace} minPace={minPace} maxPace={maxPace} />
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <ElevationIcon type={seg.elevation} />
          <CrowdBadge level={seg.crowd} />
        </div>
        <div className="min-w-[48px] flex-shrink-0 text-right text-xs font-semibold text-foundry-text-muted">
          {seg.cumulativeDisplay}
        </div>
      </div>

      {isExpanded && isPlaying && (
        <div
          key={`progress-${idx}-${storyDuration}`}
          className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden rounded-b-xl"
          style={{ background: "rgba(255,107,107,0.12)" }}
        >
          <div
            className="h-full"
            style={{
              background: "#FF6B6B",
              animation: `foundry-story-progress ${storyDuration}ms linear forwards`,
            }}
          />
        </div>
      )}

      {isExpanded && (
        <div className="mt-3.5 border-t border-white/[0.06] pt-3.5">
          <div className="font-sans text-xs leading-relaxed text-foundry-text">{seg.note}</div>
          <div className="mt-2.5 flex gap-5">
            <div>
              <span className="text-[10px] text-foundry-text-muted">SECTION </span>
              <span className="text-xs text-foundry-text-muted">{seg.label}</span>
            </div>
            <div>
              <span className="text-[10px] text-foundry-text-muted">ADJ </span>
              <span
                className="text-xs"
                style={{ color: seg.adjustment > 0 ? "#FF8A65" : "#69DB7C" }}
              >
                {seg.adjustment > 0 ? "+" : ""}
                {(seg.adjustment * 60).toFixed(0)}s
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaceBar({ pace, minPace, maxPace }: { pace: number; minPace: number; maxPace: number }) {
  const range = maxPace - minPace || 1;
  const pct = ((pace - minPace) / range) * 100;
  const hue = 140 - pct * 1.2;
  return (
    <div className="h-1.5 overflow-hidden rounded-sm bg-white/[0.06]">
      <div
        className="h-full rounded-sm transition-[width]"
        style={{
          width: `${Math.max(15, Math.min(100, pct))}%`,
          background: `hsl(${hue}, 70%, 55%)`,
        }}
      />
    </div>
  );
}

function ElevationIcon({ type }: { type: PacedSegment["elevation"] }) {
  if (type === "slight_up" || type === "steep_up") return <span style={{ color: "#FF8A65" }}>↗</span>;
  if (type === "slight_down" || type === "steep_down")
    return <span style={{ color: "#69DB7C" }}>↘</span>;
  return <span style={{ color: "#868E96" }}>→</span>;
}

function CrowdBadge({ level }: { level: PacedSegment["crowd"] }) {
  const cfg = {
    heavy: { color: "#FF6B6B", bg: "rgba(255,107,107,0.12)", label: "Congested" },
    moderate: { color: "#FFB84D", bg: "rgba(255,184,77,0.12)", label: "Moderate" },
    light: { color: "#51CF66", bg: "rgba(81,207,102,0.12)", label: "Clear" },
  }[level];
  return (
    <span
      className="rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
      style={{ color: cfg.color, background: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
}
